import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Trash2, 
  Star,
  Loader2,
  Download,
  AlertCircle,
  HelpCircle,
  Check,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

// Help Modal for Resume Management
const ResumeHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Resume Management
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This is where you manage your resume files. Your resume helps our AI understand your background and improves job matching accuracy.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">
              A complete, up-to-date resume gives our matching algorithm more context about your skills, experience, and career trajectory — leading to better V-FIT scores.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Use PDF format for best compatibility',
                'Keep your resume under 10MB',
                'Set your most current resume as "Primary"',
                'You can upload multiple versions for different roles'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ResumeManagementPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Load resumes
  useEffect(() => {
    loadResumes();
  }, []);

  const loadResumes = async () => {
    try {
      const response = await authAxios.get('/candidates/resumes');
      setResumes(response.data.resumes || []);
    } catch (error) {
      console.error('Failed to load resumes:', error);
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or DOCX file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', file.name);
    formData.append('is_primary', resumes.length === 0 ? 'true' : 'false');
    formData.append('auto_parse', 'true');  // Explicitly trigger AI parsing

    try {
      const uploadResponse = await authAxios.post('/candidates/resumes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Check if parsing succeeded and show appropriate message
      const resumeData = uploadResponse.data?.resume;
      if (resumeData?.parsed) {
        toast.success('Resume uploaded and analyzed successfully!');
      } else if (resumeData?.parse_error) {
        // Show specific error message
        toast.warning(resumeData.parse_error, { duration: 6000 });
      } else {
        toast.info('Resume uploaded! You can re-analyze it from your Profile page.');
      }
      await loadResumes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload resume';
      toast.error(message);
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleSetPrimary = async (resumeId) => {
    try {
      await authAxios.put(`/candidates/resumes/${resumeId}/set-primary`);
      toast.success('Primary resume updated');
      await loadResumes();
    } catch (error) {
      toast.error('Failed to update primary resume');
    }
  };

  const handleDelete = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) {
      return;
    }

    try {
      await authAxios.delete(`/candidates/resumes/${resumeId}`);
      toast.success('Resume deleted');
      await loadResumes();
    } catch (error) {
      toast.error('Failed to delete resume');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-heading">Resume Management</h1>
            <p className="text-slate-500">Upload and manage your resumes</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHelp(true)}
            className="text-slate-400 hover:text-lime-600 hover:bg-lime-50"
            data-testid="resume-help-btn"
          >
            <HelpCircle className="h-4 w-4 mr-1.5" />
            <span className="text-xs">Help</span>
          </Button>
        </div>

        {/* Upload Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Upload Resume</CardTitle>
            <CardDescription>PDF or DOCX format, up to 10MB</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
                data-testid="resume-upload-input"
              />
              <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                uploading ? 'border-teal-300 bg-lime-50' : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
              }`}>
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-10 w-10 text-lime-600 animate-spin" />
                    <p className="text-slate-600 font-medium">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-14 w-14 rounded-full bg-lime-50 flex items-center justify-center">
                      <Upload className="h-7 w-7 text-lime-600" />
                    </div>
                    <p className="text-slate-900 font-medium">Drop your resume here or click to upload</p>
                    <p className="text-sm text-slate-500">PDF or DOCX, max 10MB</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resume List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Your Resumes</CardTitle>
            <CardDescription>
              {resumes.length === 0 
                ? 'No resumes uploaded yet' 
                : `${resumes.length} resume${resumes.length > 1 ? 's' : ''} uploaded`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : resumes.length === 0 ? (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <FileText className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-slate-500">No resumes uploaded yet</p>
                <p className="text-sm text-slate-400">Upload your first resume above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {resumes.map((resume) => (
                  <div 
                    key={resume.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      resume.is_primary 
                        ? 'bg-lime-50 border-teal-200' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`resume-item-${resume.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        resume.is_primary ? 'bg-teal-100' : 'bg-slate-200'
                      }`}>
                        <FileText className={`h-5 w-5 ${
                          resume.is_primary ? 'text-lime-600' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 truncate">{resume.label}</p>
                          {resume.is_primary && (
                            <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 flex-shrink-0">
                              <Star className="h-3 w-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          Uploaded {formatDate(resume.uploaded_at)}
                          {resume.file_type && ` · ${resume.file_type.split('/').pop().toUpperCase()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      {!resume.is_primary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetPrimary(resume.id)}
                          className="text-slate-600 hover:text-lime-600"
                          data-testid={`set-primary-${resume.id}`}
                        >
                          <Star className="h-4 w-4" />
                          <span className="sr-only sm:not-sr-only sm:ml-2">Set Primary</span>
                        </Button>
                      )}
                      {resume.download_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="text-slate-600 hover:text-slate-900"
                        >
                          <a href={resume.download_url} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4" />
                            <span className="sr-only">Download</span>
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(resume.id)}
                        className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-resume-${resume.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <div className="mt-6 p-4 bg-slate-100 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Resume Tips</p>
              <ul className="space-y-1">
                <li>• Your primary resume is the one we use for matching</li>
                <li>• Upload different versions for different role types</li>
                <li>• Keep resumes updated for the best match results</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      
      {/* Help Modal */}
      <ResumeHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
